/* Ad conversion tracking for funnel pages AND core public forms.
 *
 * Meta Pixel base code lives in public/index.html (Pixel ID 3012784582258885)
 * so Pixel Helper detects it and PageView fires on every route automatically.
 *
 * This module is responsible for firing the named conversion events after a
 * successful backend submit.
 *
 * Google Ads is env-driven (placeholders until IDs are provided). All keys are
 * optional — if a key is missing, the corresponding gtag conversion simply does
 * NOT fire (Meta Pixel still fires from the base code on the funnel paths).
 *
 *   REACT_APP_GOOGLE_ADS_CONVERSION_ID   e.g. "AW-1234567890"
 *   REACT_APP_GADS_LABEL_CONTACT
 *   REACT_APP_GADS_LABEL_SAVINGS_ANALYSIS
 *   REACT_APP_GADS_LABEL_POS_QUOTE
 *   REACT_APP_GADS_LABEL_FREE_POS
 *   REACT_APP_GADS_LABEL_CASH_DISCOUNT
 *   REACT_APP_GADS_LABEL_GET_STARTED
 *
 * Back-compat: if the older REACT_APP_GOOGLE_ADS_ID /
 * REACT_APP_GA_FREE_POS_CONVERSION_LABEL /
 * REACT_APP_GA_CASH_DISCOUNT_CONVERSION_LABEL vars are set, they are still
 * honoured so live ad accounts keep working without a redeploy.
 */

import { getTrackingPayload } from "./utm";

const GOOGLE_ADS_ID =
  process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID ||
  process.env.REACT_APP_GOOGLE_ADS_ID ||
  "";

/* Map an internal event name to its Google Ads conversion label env var.
 * The keys here are the symbolic events fired from forms/funnels; we keep the
 * older FreePosLead / CashDiscountLead names so existing call-sites work.   */
const GA_LABELS = {
  // Existing funnel events (back-compat).
  FreePosLead:
    process.env.REACT_APP_GADS_LABEL_FREE_POS ||
    process.env.REACT_APP_GA_FREE_POS_CONVERSION_LABEL ||
    "",
  CashDiscountLead:
    process.env.REACT_APP_GADS_LABEL_CASH_DISCOUNT ||
    process.env.REACT_APP_GA_CASH_DISCOUNT_CONVERSION_LABEL ||
    "",
  // Public form events.
  ContactLead: process.env.REACT_APP_GADS_LABEL_CONTACT || "",
  SavingsAnalysisLead: process.env.REACT_APP_GADS_LABEL_SAVINGS_ANALYSIS || "",
  PosQuoteLead: process.env.REACT_APP_GADS_LABEL_POS_QUOTE || "",
  GetStartedLead: process.env.REACT_APP_GADS_LABEL_GET_STARTED || "",
};

let _initialized = false;

/* Inject Google gtag base. We do NOT auto-fire a conversion here — only on
 * submit. Skipped entirely when no conversion ID is configured. */
function _loadGoogleAds() {
  if (!GOOGLE_ADS_ID || typeof window === "undefined" || window.__gtagLoaded) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  // gtag is initialised even if the script is still loading — calls queue up.
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ADS_ID);
  window.__gtagLoaded = true;
}

export function initTracking() {
  if (_initialized) return;
  _initialized = true;
  try { _loadGoogleAds(); } catch (err) { console.warn("[tracking] Google Ads load failed:", err); }
  // Meta Pixel is already loaded from index.html — no action needed here.
}

/* Fire a named conversion event for the funnel/form.
 * Safe to call even if no IDs are configured (becomes a no-op). */
export function trackConversion(eventName) {
  if (typeof window === "undefined" || !eventName) return;

  // Meta Pixel — fire standard "Lead" with the funnel name attached, plus a
  // custom event for clarity in Events Manager. Pixel base code is always
  // present in index.html so this fires regardless of Google Ads config.
  if (window.fbq) {
    try {
      window.fbq("track", "Lead", { funnel: eventName });
      window.fbq("trackCustom", eventName);
    } catch (err) { console.warn("[tracking] fbq conversion failed:", err); }
  }

  // Google Ads — fire conversion only when both the conversion ID and the
  // per-event label are configured. If either is missing, this is a no-op.
  const label = GA_LABELS[eventName];
  if (window.gtag && GOOGLE_ADS_ID && label) {
    try {
      window.gtag("event", "conversion", {
        send_to: `${GOOGLE_ADS_ID}/${label}`,
        event_callback: () => {},
      });
    } catch (err) { console.warn("[tracking] gtag conversion failed:", err); }
  }
}

/* Push a GTM dataLayer event for a successfully submitted lead. Call this
 * ONLY after the backend returned a 200 — never on button click. The GTM
 * container (index.html) initialises window.dataLayer; if the container
 * isn't loaded (ad-blocked user), we still push safely so it queues.
 *
 * Payload shape (fixed schema — Marketing team relies on these exact keys).
 * All values are marketing-safe only — no SSN, no files, no notes, no bank
 * info, no onboarding docs. lead_id is the backend/CRM lead id returned by
 * the axios.post response; if unavailable, we push the event without it
 * rather than break the form.
 */
export function pushLeadDataLayer(eventName, leadType, leadId) {
  if (typeof window === "undefined" || !eventName) return;
  try {
    const t = getTrackingPayload();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      lead_id: leadId || null,
      lead_type: leadType || null,
      source_label: t.last_touch_source || null,
      utm_source: t.utm_source || null,
      utm_medium: t.utm_medium || null,
      utm_campaign: t.utm_campaign || null,
      utm_term: t.utm_term || null,
      utm_content: t.utm_content || null,
      landing_page_url: t.landing_page_url || null,
      first_touch_source: t.first_touch_source || null,
    });
  } catch (err) { console.warn("[tracking] dataLayer push failed:", err); }
}
